import React, { useEffect, useRef, useState } from 'react';
import { ATLAS_BACKEND_URL } from '../../config';
import { esBackendDePruebaPermitido } from './backendPermitido';
import { aplicarPoll, celdasPendientes, configBaseId, corridaTerminada, crearCeldas, creditosDeCorrida, imagenesDeCorrida, poseDeCelda, reintentarCelda } from './celdas';
import { crearColaGuardado, type ColaGuardado } from './colaGuardado';
import { ejecutarCorrida } from './ejecutar';
import { agruparPorExecution, formatearCosto, formatearTiempo, metricasDeCelda, resumenDeCorrida } from './metricasDeCelda';
import { crearCorrida, guardarCeldas, listarCorridas, pollFilas, postGenerateContra } from './setsApi';
import type { Celda, Corrida, TestSet } from './tipos';

/** Ejecución y progreso de corridas. El disparo es reanudable (celdas 'pendiente'
 * se disparan al reabrir) y la completitud la decide el poll por execution_id
 * cada 5s — nunca la respuesta del request. */

const CHIP: Record<Celda['estado'], string> = {
  pendiente: 'bg-gray-200 text-gray-600',
  disparada: 'bg-blue-100 text-blue-700 animate-pulse',
  completa: 'bg-green-100 text-green-700',
  fallida: 'bg-red-100 text-red-700',
  sin_resultado: 'bg-amber-100 text-amber-700',
};

const CorridaView: React.FC<{ idCompany: string; set: TestSet; imagenesRestantes: number }> = ({
  idCompany,
  set,
  imagenesRestantes,
}) => {
  const [corridas, setCorridas] = useState<Corrida[]>([]);
  const [activa, setActiva] = useState<Corrida | null>(null);
  const [etiqueta, setEtiqueta] = useState('');
  const [backendUrl, setBackendUrl] = useState('');
  const [disparando, setDisparando] = useState(false);
  // Tiempo y costo de las celdas de la corrida activa
  // (ADR-2026-10-13-tiempo-y-costo-en-el-test-masivo).
  const [metricas, setMetricas] = useState<ReturnType<typeof agruparPorExecution>>(new Map());
  // TODA persistencia de celdas sale por esta cola (ver colaGuardado.ts): los
  // guardados son el array completo y había hasta cuatro escritores con
  // requests en paralelo — el snapshot viejo que llegaba último pisaba al
  // nuevo, revirtiendo celdas ya disparadas. El pendiente además viaja CON el
  // id de su corrida (antes el flush usaba `activa.id` y cambiar de corrida
  // con un disparo en vuelo guardaba las celdas de A bajo el id de B).
  const colaRef = useRef<ColaGuardado | null>(null);
  if (!colaRef.current) {
    colaRef.current = crearColaGuardado(guardarCeldas, (e) =>
      // Un guardado caído en silencio deja la corrida guardada VIEJA y el
      // reanudar re-dispara: loguear siempre (misma lección que el poll).
      console.error('[test-masivo] guardado de celdas falló:', e),
    );
  }
  const cola = colaRef.current;
  // Base más fresca conocida de las celdas, POR corrida. Todo closure async que
  // derive un snapshot nuevo (el poll con su request en vuelo) parte de acá y
  // no de la `activa` que capturó al montarse: derivar de una base vieja
  // re-fabrica el array completo con celdas ya disparadas de vuelta en
  // 'pendiente' — y ese snapshot entra a la cola DESPUÉS del nuevo, o sea que
  // la serialización lo hace ganar en la DB, y reabrir re-dispara trabajo ya
  // hecho. La cola garantiza el orden; esto garantiza que lo que se encola
  // nunca sea más viejo que lo anterior.
  const celdasFrescasRef = useRef(new Map<string, Celda[]>());

  // Con la expansión por pose el total NO es productos×configs: se calcula igual
  // que la creación real de celdas. Y las IMÁGENES no son las celdas: una celda del
  // modo automático pide hasta 6 (ADR-2026-08-26-modo-automatico-en-el-test-masivo).
  const celdasDelSet = crearCeldas(set);
  const totalCeldas = celdasDelSet.length;
  const totalImagenes = imagenesDeCorrida(celdasDelSet);
  // El saldo es de créditos: con configs en 4K una imagen cuesta 2.
  const totalCreditos = creditosDeCorrida(set, celdasDelSet);

  const recargar = async () => {
    const lista = await listarCorridas(set.id);
    setCorridas(lista);
    return lista;
  };
  useEffect(() => {
    setActiva(null);
    void recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set.id]);

  // Persistencia throttled de celdas (máx ~1/s) durante el disparo. Montado UNA
  // vez: el destino sale del propio pendiente, nunca de la corrida activa.
  useEffect(() => {
    const timer = setInterval(() => void cola.flush(), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Métricas: se leen al elegir una corrida y cada vez que una celda más TERMINA. No sólo
  // las completas: una fallida también puede traer filas con costo
  // (ADR-2026-10-16-review-185-metricas-al-terminar-cada-celda). Aparte del poll de
  // completitud, que sólo mira las 'disparada'.
  const terminadasActiva =
    activa?.celdas.filter((c) => c.estado !== 'pendiente' && c.estado !== 'disparada').length ?? 0;
  useEffect(() => {
    if (!activa) return;
    let vigente = true;
    void pollFilas(activa.celdas.map((c) => c.execution_id))
      .then((filas) => {
        if (vigente) setMetricas(agruparPorExecution(filas));
      })
      .catch((e: unknown) => console.error('[test-masivo] lectura de tiempo y costo falló:', e));
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa?.id, terminadasActiva]);
  const resumen = activa ? resumenDeCorrida(activa.celdas, metricas) : null;

  // Poll de completitud cada 5s mientras la corrida activa no terminó.
  useEffect(() => {
    if (!activa || corridaTerminada(activa.celdas)) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const disparadas = (celdasFrescasRef.current.get(activa.id) ?? activa.celdas).filter(
            (c) => c.estado === 'disparada',
          );
          if (disparadas.length === 0) return;
          const filas = await pollFilas(disparadas.map((c) => c.execution_id));
          // La base se lee DESPUÉS del await (ver celdasFrescasRef): mientras el
          // request viajaba, onCeldas o el guardado final pudieron avanzar celdas.
          // De acá al set no hay más awaits, así que nadie se interpone.
          const base = celdasFrescasRef.current.get(activa.id) ?? activa.celdas;
          const celdas = aplicarPoll(base, filas, Date.now());
          if (JSON.stringify(celdas) !== JSON.stringify(base)) {
            celdasFrescasRef.current.set(activa.id, celdas);
            // Funcional y con guarda de id: la `activa` del closure puede ser
            // vieja, y pintarla entera además des-seleccionaría la corrida a la
            // que el usuario se cambió mientras el poll viajaba.
            setActiva((prev) => (prev && prev.id === activa.id ? { ...prev, celdas } : prev));
            await cola.guardar(activa.id, celdas);
          }
        } catch (e) {
          // Un poll roto que muere en silencio deja la corrida clavada en
          // "disparada" (pasó el 23-ago con una columna inexistente): loguear SIEMPRE.
          console.error('[test-masivo] poll de corrida falló:', e);
        }
      })();
    }, 5000);
    return () => clearInterval(timer);
  }, [activa]);

  const disparar = async (corrida: Corrida) => {
    // Rechazo VISIBLE antes de disparar: el catch por celda de ejecutarCorrida
    // es silencioso, y sin esto una URL no permitida marcaría todas las celdas
    // 'fallida' sin explicar por qué. setsApi igual vuelve a chequear — la
    // barrera del token vive allá; esto es solo el aviso.
    if (corrida.backend_url && !esBackendDePruebaPermitido(corrida.backend_url, ATLAS_BACKEND_URL)) {
      window.alert(
        `Backend no permitido: ${corrida.backend_url}\n\nSolo localhost (worktree local) o el backend propio — el JWT de tu sesión viaja en cada request.`,
      );
      return;
    }
    setDisparando(true);
    celdasFrescasRef.current.set(corrida.id, corrida.celdas);
    try {
      const celdas = await ejecutarCorrida({
        set,
        celdas: corrida.celdas,
        // Corrida apuntada a OTRO backend (worktree en otra versión de git) →
        // post directo a esa URL con el mismo JWT. Es la comparación de
        // versiones de prompt: A contra el viejo, B contra el actual.
        ...(corrida.backend_url
          ? { post: (body: Record<string, unknown>) => postGenerateContra(corrida.backend_url!, body) }
          : {}),
        onCeldas: (cs) => {
          celdasFrescasRef.current.set(corrida.id, cs);
          cola.anotar(corrida.id, cs);
          setActiva((prev) => (prev && prev.id === corrida.id ? { ...prev, celdas: cs } : prev));
        },
      });
      celdasFrescasRef.current.set(corrida.id, celdas);
      await cola.guardar(corrida.id, celdas);
      // Igual que onCeldas: solo si el usuario sigue mirando ESTA corrida — el
      // final de un disparo largo no tiene por qué robarle la selección.
      setActiva((prev) => (prev && prev.id === corrida.id ? { ...corrida, celdas } : prev));
    } finally {
      setDisparando(false);
    }
  };

  const nuevaCorrida = async () => {
    if (!etiqueta.trim()) return;
    const celdas = crearCeldas(set);
    const corrida = await crearCorrida(idCompany, set.id, etiqueta.trim(), celdas, backendUrl.trim() || null);
    setEtiqueta('');
    await recargar();
    setActiva(corrida);
    await disparar(corrida);
  };

  const reintentar = async (configId: string, varianteId: string) => {
    if (!activa) return;
    const celdas = reintentarCelda(
      celdasFrescasRef.current.get(activa.id) ?? activa.celdas,
      configId,
      varianteId,
    );
    celdasFrescasRef.current.set(activa.id, celdas);
    await cola.guardar(activa.id, celdas);
    const corrida = { ...activa, celdas };
    setActiva(corrida);
    await disparar(corrida);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
            placeholder='Etiqueta (ej. "antes-fix-color")'
            className="rounded-lg border border-gray-300 py-2 px-4 text-sm text-brand-black"
          />
          <button
            type="button"
            onClick={() => void nuevaCorrida()}
            disabled={disparando || !etiqueta.trim()}
            className="rounded-lg bg-brand-black py-2 px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            Ejecutar corrida
          </button>
          <span className={`text-sm ${totalCreditos > imagenesRestantes ? 'font-semibold text-red-600' : 'text-gray-500'}`}>
            {totalCeldas} celdas · {totalImagenes} {totalImagenes === 1 ? 'imagen' : 'imágenes'}
            {totalCreditos !== totalImagenes && ` · ${String(totalCreditos)} créditos`} — te quedan {imagenesRestantes}
          </span>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Backend
            <select
              value={backendUrl === '' || backendUrl === 'http://localhost:3002' ? backendUrl : 'otro'}
              onChange={(e) => setBackendUrl(e.target.value === 'otro' ? 'http://' : e.target.value)}
              className="rounded-md border border-gray-300 bg-white py-1.5 px-2 text-sm text-brand-black"
            >
              <option value="">Actual (el del front)</option>
              <option value="http://localhost:3002">Viejo — localhost:3002</option>
              <option value="otro">Otra URL…</option>
            </select>
            {backendUrl !== '' && backendUrl !== 'http://localhost:3002' && (
              <input
                value={backendUrl}
                onChange={(e) => setBackendUrl(e.target.value)}
                className="w-48 rounded-md border border-gray-300 py-1.5 px-2 text-sm text-brand-black"
                placeholder="http://localhost:3003"
              />
            )}
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {corridas.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setActiva(c)}
            className={`rounded-full border py-2 px-4 text-sm ${activa?.id === c.id ? 'border-brand-black bg-brand-black text-white' : 'border-gray-300 bg-white text-brand-black'}`}
          >
            {c.etiqueta}
            {c.backend_url && <span className="ml-1 opacity-60">@{new URL(c.backend_url).port || c.backend_url}</span>}
            <span className="ml-2 opacity-70">
              {c.celdas.filter((x) => x.estado === 'completa').length}/{c.celdas.length}
            </span>
          </button>
        ))}
        {corridas.length === 0 && <p className="text-sm text-gray-500">Sin corridas todavía.</p>}
      </div>

      {activa && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-3">
            <h3 className="font-semibold text-brand-black">{activa.etiqueta}</h3>
            {celdasPendientes(activa.celdas).length > 0 && !disparando && (
              <button type="button" onClick={() => void disparar(activa)} className="rounded-lg bg-brand-black py-2 px-4 text-xs font-medium text-white">
                Reanudar disparo ({celdasPendientes(activa.celdas).length} pendientes)
              </button>
            )}
            {disparando && <span className="text-xs text-blue-600">Disparando…</span>}
            {resumen && (resumen.costoUsd != null || resumen.conTiempo > 0) && (
              <span
                className="ml-auto text-xs tabular-nums text-gray-500"
                title="Costo de IA sumado (un piso: los modelos sin tarifa no suman) y tiempo promedio desde el disparo hasta la imagen, incluida la cola"
              >
                {formatearCosto(resumen.costoUsd)} en total · {formatearTiempo(resumen.tiempoPromedioMs)} promedio por celda
              </span>
            )}
          </div>
          <div className="space-y-2">
            {[...new Set(activa.celdas.map((c) => c.config_id))].map((configId) => {
              const base = set.configs.find((c) => c.config_id === configBaseId(configId));
              const pose = poseDeCelda(set, configId);
              const nombre = (base?.nombre ?? configId.slice(0, 8)) + (pose ? ' · pose' : '');
              const celdasConfig = activa.celdas.filter((c) => c.config_id === configId);
              return (
                <div key={configId} className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 text-xs">
                  <span className="w-48 truncate font-medium text-brand-black">{nombre}</span>
                  {celdasConfig.map((celda) => {
                    const mc = metricasDeCelda(celda, metricas);
                    return (
                    <span key={celda.variante_id + celda.execution_id} className="flex items-center gap-1">
                      <span className={`inline-block rounded-full px-2 py-0.5 ${CHIP[celda.estado]}`}>{celda.estado}</span>
                      {(mc.tiempoMs != null || mc.costoUsd != null) && (
                        <span className="tabular-nums text-[10px] text-gray-500">
                          {formatearTiempo(mc.tiempoMs)} · {formatearCosto(mc.costoUsd)}
                        </span>
                      )}
                      {celda.estado === 'sin_resultado' && (
                        <button type="button" onClick={() => void reintentar(configId, celda.variante_id)} className="text-[10px] text-blue-600 underline">
                          reintentar
                        </button>
                      )}
                    </span>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CorridaView;
